import { AddonPlatform, DataImportRunStatusType } from "@coeff/api";
import { AdminExtDataImport, DefaultApiGetDataImportsRequest } from "@coeff/api-admin-ext";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { getScheduleSummaryText, getTimeZone, useApiContext } from "../../../../src/utils";
import { QueryCacheKey, SortColumn, SortDirection } from "../../../api";
import {
  LoaderWithPerfTimings,
  Typography,
  FlexRow,
  GoogleSheet,
  Excel,
  ImportTypeIcon,
  FlexColumn,
  Tooltip,
  DataSourceInfoWrap,
  SortDropdown,
  ArrowDown,
  ChevronLeft,
  Button,
} from "../../../components";
import { AdminTable } from "../../../components/AdminTable";
import { StatusTag } from "../../../components/StatusTag";
import { COLORS, KEY_LABEL } from "../../../constants";
import { TabName } from "../../WorkspaceAdmin/WorkspaceAdmin";
import { useTableFilters } from "../useTableFilters";

import { DataImportDetailsDrawer } from "./DataImportDetailsDrawer";
import { DataImportsFilterToolbar } from "./DataImportsFilterToolbar";
import { IconDropdown } from "./IconDropdown";

const Container = styled.div`
  height: 100%;
  width: 100%;

  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  overflow: hidden;
  overflow-x: auto;
`;

const PaginationButton = styled(Button)`
  &&.ant-btn {
    border-radius: 0;
    padding: 14px 8px;
    align-items: center;
    font-size: 12px;

    &:active,
    &:focus,
    &:hover {
      border-color: ${COLORS.coeblue4};
      background-color: transparent;
    }
  }
`;

export const getSortIcon = (
  sortedColumn: { columnKey: string; value: string },
  columnKey: string
) => {
  return sortedColumn.columnKey === columnKey ? (
    sortedColumn.value === "ascend" ? (
      <ArrowDown className="sort-icon sort-icon-active" style={{ transform: "rotate(180deg)" }} />
    ) : (
      <ArrowDown className="sort-icon sort-icon-active" />
    )
  ) : (
    <ArrowDown className="sort-icon sort-icon-inactive" stroke={COLORS.black45} />
  );
};

export const DataImports: React.FC = () => {
  const history = useHistory();

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const domain = searchParams.get("domain") ?? "all_domains";

  const { adminExtApiClient } = useApiContext();

  const [search, setSearch] = useState("");

  const [activeDataImportId, setActiveDataImportId] = React.useState<string>();

  const [hoveredDataSourceId, setHoveredDataSourceId] = useState<string>();

  const { data: hoveredDataSourceResponse } = useQuery(
    [QueryCacheKey.ADMIN_EXT_DATA_SOURCE, hoveredDataSourceId],
    () => adminExtApiClient.getDataSource({ data_source_id: hoveredDataSourceId! }),
    { enabled: Boolean(hoveredDataSourceId) }
  );

  const [sortedColumn, setSortedColumn] = useState<SortColumn>({
    columnKey: "last_run_dt",
    value: SortDirection.descend,
  });

  const [page, setPage] = React.useState(0);

  const pageSize = 100;

  const [pageOffset, setPageOffset] = useState(0);

  const setPageAndOffset = (page: number) => {
    setPage(page);
    setPageOffset(page * pageSize);
  };

  const { filters, setFilters } = useTableFilters<keyof DefaultApiGetDataImportsRequest>([
    "data_source_id",
    "data_source_type",
    "user_id",
    "status",
    "schedule",
  ]);

  const onFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(0);
    setPageOffset(0);
  };

  const { data: dataImportsResponse, isLoading } = useQuery(
    [
      QueryCacheKey.ADMIN_EXT_DATA_IMPORTS,
      domain,
      pageSize,
      pageOffset,
      sortedColumn,
      search,
      filters,
    ],
    () =>
      adminExtApiClient.getDataImports({
        domain,
        page_size: pageSize,
        offset: pageOffset,
        sort_by: sortedColumn.columnKey,
        sort_order: sortedColumn.value,
        tz_name: getTimeZone(),
        search,

        // Filters
        data_source_type: filters.data_source_type as unknown as string,
        user_id: filters.user_id as unknown as string,
        status: filters.status as unknown as string,
        schedule: filters.schedule as unknown as string,
        data_source_id: filters.data_source_id as unknown as string,
      }),
    {
      enabled: Boolean(domain),
    }
  );

  const dataImports = dataImportsResponse?.data?.data_imports ?? [];

  const columns: ColumnsType<AdminExtDataImport> = [
    {
      title: <Typography noWrap>Import</Typography>,
      dataIndex: "data_import_name",
      key: "data_import_name",
      showSorterTooltip: false,
      width: 300,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="data_import_name"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "data_import_name"),
      render: (dataImportName: string, dataImport) => {
        const sheetName =
          dataImport.import_target_platform === AddonPlatform.GoogleSheets
            ? dataImport.google_sheets_file_name
            : dataImport.excel_sheets_file_name;

        const sheetUrl =
          dataImport.import_target_platform === AddonPlatform.GoogleSheets
            ? dataImport.spreadsheet_url
            : undefined;

        return (
          <FlexRow style={{ maxWidth: 300 }}>
            {dataImport.import_target_platform === AddonPlatform.GoogleSheets ? (
              <GoogleSheet height="24px" width="24px" />
            ) : (
              <Excel height="24px" width="24px" />
            )}

            <FlexColumn gap={0} minWidthZero>
              <Typography fontWeight={500} title={dataImportName} noWrap>
                {dataImportName}
              </Typography>

              <Typography
                fontSize="13px"
                color="textSecondary"
                title={sheetName}
                noWrap
                underline={Boolean(sheetUrl)}
                onClick={e => {
                  e.stopPropagation();
                  sheetUrl ? window.open(sheetUrl, "_blank") : null;
                }}
              >
                {sheetName || "-"}
              </Typography>
            </FlexColumn>
          </FlexRow>
        );
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.data_source_name}</Typography>,
      dataIndex: "data_source_name",
      key: "data_source_name",
      showSorterTooltip: false,
      width: 180,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="data_source_name"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "data_source_name"),
      render: (dataSourceName: AdminExtDataImport["data_source_name"], dataImport) => {
        return (
          <div
            style={{ maxWidth: 180 }}
            onMouseEnter={() => setHoveredDataSourceId(dataImport.data_source_id)}
            onMouseLeave={() => setHoveredDataSourceId(undefined)}
          >
            <Tooltip
              variant="light"
              placement="topLeft"
              title={
                hoveredDataSourceId && hoveredDataSourceResponse?.data.data_source ? (
                  <DataSourceInfoWrap dataSource={hoveredDataSourceResponse.data.data_source} />
                ) : undefined
              }
            >
              <FlexRow>
                <ImportTypeIcon size="small" type={dataImport.data_source_type} />

                <Typography noWrap>
                  {dataSourceName || dataImport.data_source_type_friendly_name}
                </Typography>
              </FlexRow>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: <Typography noWrap>Last Run</Typography>,
      dataIndex: "last_run_status_type",
      key: "last_run_status_type",
      showSorterTooltip: false,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="last_run_dt" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "last_run_dt"),
      render: (status: AdminExtDataImport["last_run_status_type"], dataImport) => {
        return dataImport.last_run_dt ? (
          <StatusTag
            status={status.toLowerCase() as DataImportRunStatusType}
            label={
              <Typography inline title={moment(dataImport.last_run_dt).format("MMM D, YYYY h:mma")}>
                {moment(dataImport.last_run_dt).fromNow()}
              </Typography>
            }
          />
        ) : (
          ""
        );
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.created_by}</Typography>,
      dataIndex: "created_by",
      key: "created_by",
      showSorterTooltip: false,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="created_by" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "created_by"),
      render: (createdBy: AdminExtDataImport["created_by"], dataImport) => (
        <div style={{ maxWidth: 200 }}>
          <Typography title={dataImport.created_by_email} noWrap>
            {createdBy}
          </Typography>

          {dataImport.created_dt ? (
            <>
              <Typography
                title={moment(dataImport.created_dt).format("MMM D, YYYY h:mma")}
                noWrap
                color="textSecondary"
                fontSize="13px"
              >
                {moment(dataImport.created_dt).fromNow()}
              </Typography>
            </>
          ) : null}
        </div>
      ),
    },
    {
      title: <Typography noWrap>{KEY_LABEL.is_scheduled}</Typography>,
      dataIndex: "is_scheduled",
      key: "is_scheduled",
      showSorterTooltip: false,
      width: 200,
      render: (is_scheduled, dataImport) =>
        is_scheduled ? (
          <Typography noWrap>{getScheduleSummaryText(dataImport.schedule_info)}</Typography>
        ) : (
          <Typography color="textSecondary">None</Typography>
        ),
    },
    {
      title: "",
      showSorterTooltip: false,
      width: 40,
      render: (dataImport: AdminExtDataImport) => (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <IconDropdown
            dropdownProps={{
              overlay: (
                <Menu>
                  <Menu.Item
                    onClick={() => {
                      const params = new URLSearchParams(location.search);

                      params.append("data_import_id", JSON.stringify([dataImport.data_import_id]));

                      history.push({
                        pathname: location.pathname
                          .split("/")
                          .slice(0, -1)
                          .concat(TabName.runs)
                          .join("/"),
                        search: params.toString(),
                      });
                    }}
                  >
                    View Run History
                  </Menu.Item>

                  <Menu.Item
                    onClick={() => {
                      window.open(dataImport.spreadsheet_url, "_blank");
                    }}
                  >
                    Open Sheet
                  </Menu.Item>
                </Menu>
              ),
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Container>
      <DataImportsFilterToolbar
        domain={domain}
        initialSearch={search}
        onSearchChange={value => setSearch(value)}
        filters={filters}
        setFilters={onFilterChange}
      />

      <FlexColumn gap={0.5} style={{ overflow: "hidden" }}>
        <AdminTable
          onRow={dataImport => ({
            onClick: () => {
              setActiveDataImportId(dataImport.data_import_id);
            },
          })}
          columns={columns}
          rowKey={r => r.data_import_id}
          dataSource={dataImports?.map(d => ({ ...d, key: d.data_import_id })) ?? []}
          loading={false}
          locale={{
            emptyText: isLoading ? (
              <LoaderWithPerfTimings name="DataImports.locale" />
            ) : (
              "No imports found"
            ),
          }}
          pagination={false}
        />

        <FlexRow style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <div />

          <FlexRow gap={0.5}>
            <PaginationButton
              type="text"
              size="small"
              disabled={page === 0}
              onClick={() => {
                setPageAndOffset(page - 1);
              }}
            >
              <ChevronLeft style={{ height: 11 }} />
            </PaginationButton>

            <PaginationButton
              type="text"
              size="small"
              disabled={!dataImportsResponse?.data.has_more}
              onClick={() => {
                setPageAndOffset(page + 1);
              }}
            >
              <ChevronLeft style={{ height: 11, transform: "rotate(180deg)" }} />
            </PaginationButton>
          </FlexRow>
        </FlexRow>
      </FlexColumn>

      <DataImportDetailsDrawer
        dataImportId={activeDataImportId}
        onClose={() => setActiveDataImportId(undefined)}
      />
    </Container>
  );
};
